// assets/sticker-upload-redirect.js
import { saveArtwork } from '@theme/sticker-artwork-store';
import { isValidQrDestination } from '@theme/qr-destination-field';

// A generous ceiling for a source photo, well above what any legitimate design upload needs, but
// low enough to catch the most common mobile failure mode before it ever reaches IndexedDB: a
// full-resolution camera photo (easily 15-25MB+ on a modern phone) that then exceeds the
// browser's storage quota — particularly tight/unpredictable in Safari private browsing.
const MAX_ARTWORK_BYTES = 25 * 1024 * 1024;

/**
 * The variant a shopper currently has selected, for carrying onto the design page.
 *
 * Read from a CHECKED option input rather than the two more obvious sources, both of which are wrong
 * in exactly the case that matters — changing Size when the tiers differ between sizes.
 *
 * This product's Quantity tiers aren't shared across Sizes: 2 Inches offers 14 pcs where 3 Inches
 * starts at 15 and 4 Inches at 8. The page loads on 2 Inches / 14 pcs, so when a shopper picks
 * 4 Inches the pairing "4 Inches + 14 pcs" doesn't exist as a variant at all. Liquid's
 * product_option_value.variant is therefore nil for that value, its data-variant-id renders empty,
 * and variant-picker.js — which reads that same attribute off the clicked option — takes its empty
 * branch and calls url.searchParams.delete('variant'). So:
 *
 *  - the URL has no ?variant= to read: it was actively stripped, not merely stale;
 *  - and <variant-picker>'s embedded JSON only refreshes once fetchUpdatedSection's network round
 *    trip returns and morphs in, so it can still describe the previous selection.
 *
 * The checked input is the one source that settles correct. The picker re-renders from the server,
 * which resolves the combination to a real variant (4 Inches lands on its own first tier), and the
 * markup comes back with that value checked and its own data-variant-id filled in. Both other
 * sources stay as fallbacks: the blob is right on a first load before anything is clicked, and the
 * URL is right when the page was opened on an explicit ?variant= link.
 *
 * Reported as picking 4 Inches on the product page and arriving at the design page on 2 Inches.
 *
 * @returns {string | undefined}
 */
/**
 * The option values a shopper currently has selected, as Shopify's own option-value ids.
 *
 * One per option, in the order the picker renders them. Each option renders twice on this page (a
 * desktop copy and a mobile one — see design-variant-option.liquid's dual render), and both share a
 * radio group, so they always agree; the first of each option name is taken and the duplicate
 * skipped.
 *
 * Handles both radios and <select>-based option styles, since the same block can render either
 * depending on its Style setting.
 *
 * @returns {string[]}
 */
function readSelectedOptionValueIds() {
  /** @type {Set<string>} */
  const seenOptions = new Set();
  /** @type {string[]} */
  const ids = [];

  for (const element of document.querySelectorAll('variant-picker [data-option-value-id]')) {
    const isCheckedInput = element instanceof HTMLInputElement && element.checked;
    const isSelectedOption = element instanceof HTMLOptionElement && element.selected;
    if (!isCheckedInput && !isSelectedOption) continue;

    const optionName = /** @type {HTMLElement} */ (element).dataset.optionName ?? '';
    if (seenOptions.has(optionName)) continue;

    const id = /** @type {HTMLElement} */ (element).dataset.optionValueId;
    if (!id) continue;

    seenOptions.add(optionName);
    ids.push(id);
  }

  return ids;
}

function readSelectedVariantId() {
  /** @type {Map<string, { hasChecked: boolean, firstId: string | undefined }>} */
  const groups = new Map();

  for (const input of document.querySelectorAll('variant-picker input[data-option-name]')) {
    if (!(input instanceof HTMLInputElement)) continue;

    // A checked value that resolves to a variant IS the selection — nothing below can beat it.
    if (input.checked && input.dataset.variantId) return input.dataset.variantId;

    const name = input.dataset.optionName ?? '';
    const group = groups.get(name) ?? { hasChecked: false, firstId: undefined };
    if (input.checked) group.hasChecked = true;
    if (!group.firstId && input.dataset.variantId) group.firstId = input.dataset.variantId;
    groups.set(name, group);
  }

  // Nothing checked resolves, which happens whenever the current selection is a combination that
  // doesn't exist. Picking 4 Inches while 14 pcs is active is exactly that: 14 pcs is a 2-inch tier,
  // so Shopify returns Size checked with no variant id, Quantity with NOTHING checked, and a literal
  // `null` for the picker's own JSON. There is no selected variant to carry, and the shopper usually
  // hasn't noticed their quantity was silently cleared.
  //
  // The ids that ARE still on the page have a direction to them, which matters. An id sitting on an
  // option that already has a checked sibling describes the variant you'd get by switching AWAY from
  // the current selection — the "2 Inches" radio carries a 2-inch variant precisely because clicking
  // it would take you there. Following one of those would undo the shopper's choice, which is the
  // bug this is meant to fix.
  //
  // The group with NO checked value is the unresolved one, and its ids are rendered in the context of
  // everything that IS selected — so with 4 Inches chosen, the Quantity radios carry 4-inch variants.
  // Taking the first keeps the size the shopper picked, at the cost of landing on that size's lowest
  // tier rather than a quantity they chose.
  //
  // That trade is deliberate: arriving on 4 Inches at the wrong quantity is visible and adjustable on
  // the design page, where arriving on 2 Inches is neither obvious nor what they asked for. The real
  // cure is upstream — quantity tiers shared across sizes, or the picker auto-selecting a valid tier
  // when the current one stops existing, so the page never sits in this state at all.
  for (const group of groups.values()) {
    if (!group.hasChecked && group.firstId) return group.firstId;
  }

  const variantJson = document.querySelector('variant-picker script[type="application/json"]')?.textContent;
  if (variantJson) {
    try {
      const id = JSON.parse(variantJson)?.id;
      if (id) return String(id);
    } catch {
      // fall through to the URL below
    }
  }

  return new URL(window.location.href).searchParams.get('variant') || undefined;
}

/**
 * Wraps the file input on the plain product page (blocks/file-upload.liquid). Picking a file
 * here doesn't process it in place — it persists the raw file to IndexedDB (a real browser
 * navigation is about to discard all JS state) and redirects to the dedicated design page
 * (templates/product.sticker-design.json), which reads it back on load. See
 * assets/sticker-artwork-store.js for why IndexedDB instead of session/local storage.
 */
class StickerUploadRedirect extends HTMLElement {
  connectedCallback() {
    const input = this.querySelector('input[type="file"]');
    if (!(input instanceof HTMLInputElement)) return;
    input.addEventListener('change', this.#handleChange);

    // The "Upload artwork" button is the <label for=…> of that hidden input, so the click that opens
    // the file dialog is the label's — intercept it there (see #qrDestinationBlocks) so a shopper
    // on the QR sticker page is told about a missing/invalid destination BEFORE a file dialog
    // opens, not after they've gone to the trouble of picking a file.
    const label = this.querySelector('label[for]');
    if (label instanceof HTMLLabelElement) label.addEventListener('click', this.#handleLabelClick);
  }

  disconnectedCallback() {
    const input = this.querySelector('input[type="file"]');
    if (input instanceof HTMLInputElement) input.removeEventListener('change', this.#handleChange);

    const label = this.querySelector('label[for]');
    if (label instanceof HTMLLabelElement) label.removeEventListener('click', this.#handleLabelClick);
  }

  /**
   * Whether a QR scan destination field on the page (blocks/qr-destination.liquid) is stopping the
   * upload — and, when it is, tells the shopper why and puts them in the field.
   *
   * A QR sticker without a destination is meaningless: the design page composites the QR panel
   * from this very value, and there is nothing sensible to show or print until it exists. The
   * value rides to the design page through the generic `[name^="properties["]` loop in
   * #handleChange like every other property, so nothing below carries it specially — this only
   * gates. The field's own element normalises the typed text first (`example.com` ->
   * `https://example.com`, and sets the custom validity that checkValidity reflects); the
   * isValidQrDestination re-check covers the moment before that element has upgraded, when
   * checkValidity would only be the browser's looser type="url" opinion. Pages without the field
   * (the plain Custom Stickers page) never get here past the first line.
   *
   * @returns {boolean} true when the upload must not proceed
   */
  #qrDestinationBlocks() {
    const input = document.querySelector('[data-qr-destination-input]');
    if (!(input instanceof HTMLInputElement)) return false;

    /** @type {{ commit?: () => boolean } | null} */
    const field = input.closest('qr-destination-field');
    field?.commit?.();

    // An empty value only blocks when the field is required — checkValidity already says so via the
    // native `required` message. With the block's "Required" setting off, an empty field means "no
    // code", which the design page renders as the plain artwork, and must not hold the upload.
    if (input.checkValidity() && (input.value === '' || isValidQrDestination(input.value))) return false;

    input.reportValidity();
    input.focus({ preventScroll: false });
    return true;
  }

  /** @param {MouseEvent} event */
  #handleLabelClick = (event) => {
    if (this.#qrDestinationBlocks()) event.preventDefault();
  };

  /** @param {Event} event */
  #handleChange = async (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const file = input.files?.[0];
    if (!file) return;

    // Second line of defence for the QR destination (the label click above is the first): the
    // dialog can still open by other routes — keyboard activation of the label, or a browser that
    // dispatched the input's own click — and a file picked with no destination must not be saved
    // and redirected. Clearing the input means the same file can be re-picked once the field is
    // fixed (`change` wouldn't fire for an identical re-selection otherwise).
    if (this.#qrDestinationBlocks()) {
      input.value = '';
      return;
    }

    if (file.size > MAX_ARTWORK_BYTES) {
      input.value = '';
      window.alert('That image is too large. Please choose a file under 25MB.');
      return;
    }

    // saveArtwork can genuinely reject — most commonly IndexedDB storage quota exceeded (easy to
    // hit with a full-resolution mobile camera photo, especially in Safari private browsing,
    // which caps IndexedDB much more aggressively than normal browsing). Uncaught, that silently
    // aborts this whole async handler before the redirect below ever runs — from the customer's
    // side, tapping the upload button just does nothing, with no error and no way to tell why.
    try {
      await saveArtwork(file);
    } catch (error) {
      console.error('Failed to save the uploaded artwork:', error);
      input.value = '';
      window.alert(
        "Sorry, we couldn't process that image. If you're browsing in private/incognito mode, " +
          'please try again in a normal browser window — some browsers block the storage this page ' +
          'needs while in that mode. Otherwise, please try again or use a smaller file.'
      );
      return;
    }

    const designUrl = new URL(this.dataset.designUrl ?? '', window.location.origin);

    // The URL's own ?variant= comes first, because it is the only source that is correct the instant
    // a shopper changes an option. variant-picker.js reads the id straight off the clicked option's
    // dataset and writes it here via history.replaceState with nothing but a main-thread yield in
    // the way (see its handleOptionChange), while the embedded JSON blob used below only refreshes
    // once fetchUpdatedSection's network round trip returns and morphs it in.
    //
    // Between those two moments the blob still describes the PREVIOUS selection, and this handler
    // could read it and send the shopper to the design page on the wrong variant — reported as
    // picking 4 Inches on the product page and arriving with 2 Inches (the default) selected.
    //
    // The blob stays as the fallback: on a first load nothing has been picked yet, so the URL
    // carries no variant while the blob already describes the default the page rendered with.
    // Buy-buttons' hidden input is deliberately not consulted for either — that block is separate
    // and may not be on the page at all (eg. disabled), whereas both of these always are.
    // option_values, not variant. Every attempt to carry a variant id from this page has been wrong
    // in some state, because a variant id is a DERIVED value: it only exists when the whole
    // combination resolves, it is rendered per option value in the context of the other selections
    // (so an unchecked radio's id describes switching AWAY from the current choice), and it lags a
    // network round trip behind the radios themselves. Selecting 4 Inches at 100 pcs still shipped
    // the shopper a 2-inch variant id.
    //
    // A checked radio's own data-option-value-id has none of those problems: it is exactly what the
    // shopper picked, correct the instant they pick it, and true even for a combination that doesn't
    // resolve to any variant. Shopify accepts these on the product URL and reselects them server
    // side, which is the same mechanism variant-picker.js uses for its own refetches, so the design
    // page can resolve the variant itself instead of being told a possibly-stale answer.
    const optionValueIds = readSelectedOptionValueIds();
    if (optionValueIds.length) {
      designUrl.searchParams.set('option_values', optionValueIds.join(','));
    } else {
      // Only when there are no option inputs to read at all (a single-variant product renders none).
      const variantId = readSelectedVariantId();
      if (variantId) designUrl.searchParams.set('variant', String(variantId));
    }

    // Vinyl/Size/Quantity are real variants, already carried over via ?variant= above — but
    // Shape/Finish (and any other custom-option field) are line-item properties, not encoded in
    // the variant id, so they need to travel separately. The design page reads these same query
    // params back on load (see sections/product-design-editor.liquid) to pre-select the matching
    // radio/select for each one instead of resetting to its default. Read straight off the inputs
    // (not via the cart form's FormData) since custom-option fields only reference their form by
    // its `form="..."` attribute — that form may not even be on the page if the buy-buttons block
    // happens to be disabled, but the fields themselves still render regardless.
    for (const field of document.querySelectorAll('[name^="properties["]')) {
      if (!(field instanceof HTMLInputElement) && !(field instanceof HTMLSelectElement)) continue;
      if (field instanceof HTMLInputElement && (field.type === 'radio' || field.type === 'checkbox') && !field.checked) {
        continue;
      }

      const match = field.name.match(/^properties\[(.+)\]$/);
      if (match?.[1] && field.value) designUrl.searchParams.set(match[1], field.value);
    }

    // The design page (templates/product.sticker-design.json) renders via layout/sticker-design.liquid,
    // which has no header/footer but does render the bag drawer (the editor opens it there after an
    // add), and snippets/theme-drawer.liquid restores a persisted open drawer on arrival at >= 990px —
    // over the editor's option panel. Leaving the drawer open here would also carry into the outgoing
    // cross-document view transition's snapshot, then abruptly vanish once the new page takes over —
    // so close it first and let the redirect happen once it's done.
    const cartDrawer = /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      document.getElementById('cart-drawer')
    );
    if (cartDrawer?.isOpen) await cartDrawer.close();

    window.location.href = designUrl.toString();
  };
}

customElements.define('sticker-upload-redirect', StickerUploadRedirect);
